import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { Difficulty, GameState } from '../../core/types';
import type { CommandHandler } from '../App';
import { generateExpeditionSeed } from '../randomSeed';

export function TitleScreen({
  onCommand,
  savedRun,
  saveError,
  deepeningUnlocked,
  onContinue,
  onClearSave,
}: {
  onCommand: CommandHandler;
  savedRun?: GameState;
  saveError?: string;
  deepeningUnlocked: boolean;
  onContinue: () => void;
  onClearSave: () => void;
}) {
  const [seed, setSeed] = useState(generateExpeditionSeed);
  const [difficulty, setDifficulty] = useState<Difficulty>('standard');
  const [confirmingReplacement, setConfirmingReplacement] = useState(false);
  const startButton = useRef<HTMLButtonElement>(null);
  const cancelReplacementButton = useRef<HTMLButtonElement>(null);
  const confirmReplacementButton = useRef<HTMLButtonElement>(null);
  const replacementWasOpen = useRef(false);

  useEffect(() => {
    if (confirmingReplacement) {
      replacementWasOpen.current = true;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      cancelReplacementButton.current?.focus();
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    if (replacementWasOpen.current) {
      replacementWasOpen.current = false;
      startButton.current?.focus();
    }
    return undefined;
  }, [confirmingReplacement]);

  const handleReplacementKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setConfirmingReplacement(false);
      return;
    }
    if (event.key !== 'Tab') return;
    if (event.shiftKey && document.activeElement === cancelReplacementButton.current) {
      event.preventDefault();
      confirmReplacementButton.current?.focus();
    } else if (!event.shiftKey && document.activeElement === confirmReplacementButton.current) {
      event.preventDefault();
      cancelReplacementButton.current?.focus();
    }
  };

  const startRun = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!seed.trim()) {
      onCommand({ type: 'start_run', seed, difficulty });
      return;
    }
    if (savedRun) {
      setConfirmingReplacement(true);
      return;
    }
    onCommand({ type: 'start_run', seed, difficulty });
  };

  return (
    <main id="main-content" className="title-screen">
      <div className="title-screen__atmosphere" aria-hidden="true">
        <span className="shaft shaft--one" />
        <span className="shaft shaft--two" />
        <span className="ember ember--one" />
        <span className="ember ember--two" />
        <span className="ember ember--three" />
      </div>
      <section className="title-hero" aria-labelledby="game-title">
        <p className="kicker">An independent Cathedris prototype</p>
        <div className="title-mark" aria-hidden="true"><i /><span /><i /></div>
        <h1 id="game-title">Cathedris</h1>
        <p className="title-hero__subtitle">The Bellrose Line</p>
        <p className="title-hero__lede">
          The Ichor shipments have stopped. Follow the line across Bellrose,
          keep your Gear working, and bring the missing crew home.
        </p>

        <form className="seed-form" onSubmit={startRun}>
          <label htmlFor="run-seed">Expedition seed</label>
          <div className="seed-form__row">
            <input
              id="run-seed"
              value={seed}
              maxLength={36}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setSeed(event.target.value.toUpperCase())}
            />
            <button
              className="secondary-action seed-refresh"
              type="button"
              onClick={() => setSeed(generateExpeditionSeed())}
            >
              New seed
            </button>
            <button ref={startButton} className="primary-action" type="submit">
              {savedRun ? 'Start new' : 'Begin expedition'}
            </button>
          </div>
          <p>New seeds are random. Reuse or type any seed to replay the same draws, encounters, and rewards.</p>
          {deepeningUnlocked ? (
            <fieldset className="difficulty-select">
              <legend>Expedition pressure</legend>
              <label>
                <input type="radio" name="difficulty" checked={difficulty === 'standard'} onChange={() => setDifficulty('standard')} />
                <span><strong>Standard</strong><small>The intended first-victory balance.</small></span>
              </label>
              <label>
                <input type="radio" name="difficulty" checked={difficulty === 'deepening'} onChange={() => setDifficulty('deepening')} />
                <span><strong>Deepening</strong><small>Enemies have 15% more HP and begin with 1 Strength.</small></span>
              </label>
            </fieldset>
          ) : null}
        </form>

        {savedRun?.run ? (
          <button className="continue-action" type="button" onClick={onContinue}>
            <span>Continue expedition</span>
            <small>
              Act {savedRun.run.act} · Depth {savedRun.run.node + 1} · {savedRun.run.hp}/{savedRun.run.maxHp} HP
            </small>
          </button>
        ) : null}

        {confirmingReplacement ? (
          <div className="confirmation-backdrop">
            <div
              className="save-warning"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="replace-save-title"
              aria-describedby="replace-save-description"
              onKeyDown={handleReplacementKeys}
            >
              <strong id="replace-save-title">Replace the saved expedition?</strong>
              <p id="replace-save-description">Starting with seed {seed} permanently replaces the run shown above.</p>
              <span>
                <button ref={cancelReplacementButton} className="text-action" type="button" onClick={() => setConfirmingReplacement(false)}>
                  Keep saved run
                </button>
                <button
                  ref={confirmReplacementButton}
                  className="secondary-action"
                  type="button"
                  onClick={() => onCommand({ type: 'start_run', seed, difficulty })}
                >
                  Replace and begin
                </button>
              </span>
            </div>
          </div>
        ) : null}

        {saveError ? (
          <div className="save-recovery" role="alert">
            <strong>Saved expedition unavailable</strong>
            <p>{saveError}</p>
            <button className="text-action" type="button" onClick={onClearSave}>
              Clear unusable save
            </button>
          </div>
        ) : null}

        <ul className="title-features" aria-label="Expedition mechanics">
          <li><strong>Stoke</strong><span>Build Heat for stronger effects</span></li>
          <li><strong>Temper</strong><span>Spend Heat before it burns you</span></li>
          <li><strong>Maintain</strong><span>Protect and repair persistent Gear</span></li>
        </ul>
      </section>
      <p className="build-note">Prototype v1 · Autosaves on this device · No account required</p>
      <p className="world-credit">An independent prototype set in <a href="https://www.worldanvil.com/w/cathedris-stormbril" target="_blank" rel="noreferrer">Cathedris</a>, created by Devon “Stormbril” Ruscheinski. Unofficial adaptation; no endorsement implied.</p>
    </main>
  );
}
